/**
 * Cookie Consent Manager compliant with GDPR & ePrivacy Directive.
 * Stores consent state in a versioned cookie with 1-year expiry and localStorage fallback.
 */

export const CONSENT_COOKIE_NAME = "loyalty_cookie_consent";
const CONSENT_MAX_AGE_SECONDS = 31536000; // 1 year
const CONSENT_VERSION = 1;

export interface ConsentedCategory {
  [serviceId: string]: boolean;
}

export interface ConsentCategories {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  services?: {
    analytics?: ConsentedCategory;
    marketing?: ConsentedCategory;
  };
}

export interface ConsentState {
  version: number;
  timestamp: number;
  categories: ConsentCategories;
}

export const DEFAULT_CONSENT_STATE: ConsentState = {
  version: CONSENT_VERSION,
  timestamp: 0,
  categories: {
    necessary: true,
    analytics: false,
    marketing: false,
    services: {
      analytics: {
        googleAnalytics: false,
        clarity: false,
      },
      marketing: {
        facebook: false,
        googleAds: false,
        googleMaps: false,
      },
    },
  },
};

export const COOKIE_SETTINGS_EVENT = "open_cookie_settings";
export const COOKIE_CONSENT_UPDATED_EVENT = "cookie_consent_updated";

function base64UrlEncode(str: string): string {
  try {
    return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return encodeURIComponent(str);
  }
}

function base64UrlDecode(str: string): string {
  try {
    const padding = "===".slice(0, (4 - (str.length % 4)) % 4);
    return atob(str.replace(/-/g, "+").replace(/_/g, "/") + padding);
  } catch {
    return decodeURIComponent(str);
  }
}

export function isValidConsentState(value: unknown): value is ConsentState {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  if (typeof v.timestamp !== "number" || v.timestamp <= 0) return false;
  const c = v.categories;
  if (!c || typeof c !== "object") return false;
  const cats = c as Record<string, unknown>;
  if (cats.necessary !== true) return false;
  return typeof cats.analytics === "boolean" && typeof cats.marketing === "boolean";
}

function setCookie(value: string, maxAge: number): void {
  if (typeof document === "undefined") return;
  const cookieDomain = (import.meta as unknown as { env?: { VITE_COOKIE_DOMAIN?: string } }).env?.VITE_COOKIE_DOMAIN;
  const domainAttr = cookieDomain ? `; Domain=${cookieDomain}` : "";
  document.cookie = `${CONSENT_COOKIE_NAME}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${domainAttr}`;
}

export function getConsentState(): ConsentState | null {
  if (typeof document === "undefined") return null;

  // 1. Try reading from cookie
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CONSENT_COOKIE_NAME}=([^;]*)`));
  if (match?.[1]) {
    try {
      const decoded = base64UrlDecode(decodeURIComponent(match[1]));
      const parsed = JSON.parse(decoded) as unknown;
      if (isValidConsentState(parsed)) {
        return parsed;
      }
    } catch {
      // Fallback below
    }
  }

  // 2. Fallback to localStorage
  try {
    const local = localStorage.getItem(CONSENT_COOKIE_NAME);
    if (local) {
      const parsed = JSON.parse(local) as unknown;
      if (isValidConsentState(parsed)) {
        return parsed;
      }
    }
  } catch {
    // Ignore error
  }

  return null;
}

export function setConsentState(categories: {
  analytics: boolean;
  marketing: boolean;
  services?: {
    analytics?: ConsentedCategory;
    marketing?: ConsentedCategory;
  };
}): ConsentState {
  const state: ConsentState = {
    version: CONSENT_VERSION,
    timestamp: Date.now(),
    categories: {
      necessary: true,
      analytics: categories.analytics,
      marketing: categories.marketing,
      services: categories.services ?? {
        analytics: {
          googleAnalytics: categories.analytics,
          clarity: categories.analytics,
        },
        marketing: {
          facebook: categories.marketing,
          googleAds: categories.marketing,
          googleMaps: categories.marketing,
        },
      },
    },
  };

  if (typeof document !== "undefined") {
    const serialized = JSON.stringify(state);
    setCookie(base64UrlEncode(serialized), CONSENT_MAX_AGE_SECONDS);
    try {
      localStorage.setItem(CONSENT_COOKIE_NAME, serialized);
    } catch {
      // Ignore localStorage quotas
    }
    window.dispatchEvent(
      new CustomEvent(COOKIE_CONSENT_UPDATED_EVENT, { detail: state })
    );
  }

  return state;
}

export function hasConsented(): boolean {
  return getConsentState() !== null;
}

export function isCategoryAllowed(category: "necessary" | "analytics" | "marketing"): boolean {
  if (category === "necessary") return true;
  const state = getConsentState();
  if (!state) return false;
  return state.categories[category] === true;
}

export function openCookieSettings(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(COOKIE_SETTINGS_EVENT));
  }
}
